import { useEntities, useEntity } from "@ui/hooks/serviceUtils";
import useDataManager, { DataManagerProps } from "@ui/hooks/useDataManager";
import { ISupplierBooking } from "./useSupplierBooking";
import { IFeedback } from "./useFeedBack";
import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import axios from "axios";

export interface IAdhocServing {
  id: string;
  employee: { id: string };
  status: string;
  meal: string;
  date: DateTime;
  supplierBooking?: ISupplierBooking | null;
  requestedAt: DateTime;
  feedback?: IFeedback | null;
}

const key = "adhocServing";
const endpoint = "/api/adhocserving";

export const useAdhocServingsQuery = (
  props: DataManagerProps<IAdhocServing> = {},
) => useDataManager<IAdhocServing>(key, `${endpoint}/query`, props);

export const useAdhocServings = () => useEntities<IAdhocServing>(key, endpoint);

export const useAdhocServing = (
  id: string | undefined,
  toast: boolean = true,
) =>
  useEntity<IAdhocServing>(
    key,
    endpoint,
    id,
    toast
      ? {
          add: "Ad-hoc serving added",
          update: "Ad-hoc serving updated",
          delete: "Ad-hoc serving deleted",
        }
      : undefined,
  );

export const useEmployeeAdhocServing = (
  date: DateTime,
  meal: string,
  userId: string | undefined,
) => {
  const formattedDate = date.toISODate() ?? "";

  return useQuery<any, Error>({
    queryKey: [key, meal, "booking", formattedDate, userId],
    queryFn: async () => {
      if (!userId || !formattedDate || !meal) return null;

      const url = `${endpoint}/adhoc-employee?date=${formattedDate}&meal=${encodeURIComponent(meal)}&employeeId=${encodeURIComponent(userId)}`;
      const { data } = await axios.get<any>(url);

      return data;
    },
    enabled: Boolean(formattedDate && meal && userId),
  });
};

export const useAdhocServingMeals = (date: DateTime, meal: string) => {
  const formattedDate =
    date instanceof DateTime
      ? date.toISODate()
      : date
        ? DateTime.fromJSDate(date).toISODate()
        : null;

  return useQuery<any, Error>({
    queryKey: [key, meal, "booking", formattedDate],
    queryFn: async () => {
      const url = `${endpoint}/adhoc-filter?date=${formattedDate}&meal=${meal}`;
      const { data } = await axios.get<any>(url);
      return data;
    },
    enabled: Boolean(formattedDate && meal),
  });
};
export const useAdhocRecentServings = (
  employeeId?: string,
  options?: { enabled?: boolean },
) =>
  useQuery<IAdhocServing[], Error>({
    queryKey: [key, "adhoc-recent", employeeId],
    queryFn: async () => {
      const url = `${endpoint}/adhoc-recent${employeeId ? `?employeeId=${employeeId}` : ""}`;
      const response = await axios.get<IAdhocServing[]>(url);
      return response.data;
    },
    enabled: options?.enabled ?? true,
  });
